import { z } from 'zod';
import { safeProjectPath } from './project-artifact.js';
export const reviewDimensionSchema = z.enum([
    'concept_application',
    'edge_case_reasoning',
    'explanation_quality',
    'code_clarity',
]);
export const reviewDimensionLabels = {
    concept_application: 'Concept application',
    edge_case_reasoning: 'Edge-case reasoning',
    explanation_quality: 'Explanation quality',
    code_clarity: 'Code clarity',
};
const statement = z.string().trim().min(1).max(1000);
/** One sentence naming the deliverable that is absent or incorrect, distinct from the concept gap. */
const missingStatement = z.string().trim().min(1).max(500);
/** Final-review comparison of the learner's submission with the supplied reference solution. */
export const solutionNotesSchema = z.string().trim().min(1).max(3000);
/** Learner-safe criterion title attached by the server from the authored rubric. */
export const criterionLabelSchema = z.string().trim().min(1).max(80);
const criterionId = z.string().regex(/^[a-z0-9][a-z0-9-]{0,63}$/);
export const reviewEvidenceSchema = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('source'),
        path: z.string().max(256).refine(safeProjectPath),
        startLine: z.number().int().positive(),
        endLine: z.number().int().positive(),
        quote: z
            .string()
            .min(1)
            .max(1500)
            .refine((text) => text.trim().length > 0),
    })
        .strict()
        .refine((item) => item.endLine >= item.startLine && item.endLine - item.startLine < 30),
    z.object({ kind: z.literal('explanation'), quote: statement }).strict(),
]);
const citations = z.array(reviewEvidenceSchema).min(1).max(3);
export const reviewCriterionSchema = z
    .object({
    id: criterionId,
    result: z.enum(['met', 'partial', 'not_met']),
    feedback: statement,
    citations,
})
    .strict();
const gapFields = {
    criterionId,
    concept: z.string().trim().min(1).max(200),
    evidence: statement,
    citations,
    whyItMatters: statement,
    question: statement,
    nextStep: statement,
};
/** Coaching shape of reviews stored before `missing` existed; never written again. */
const reviewGapV2Schema = z.object(gapFields).strict();
export const reviewGapSchema = z.object({ ...gapFields, missing: missingStatement }).strict();
const strengthSchema = z.object({ criterionId, description: statement, citations }).strict();
/** Model output contains observations; the application owns metrics and the verdict. */
const draftFields = {
    feedback: z.string().trim().min(1).max(2000),
    criteria: z.array(reviewCriterionSchema).min(1).max(8),
    strengths: z.array(strengthSchema).max(3),
    nextSteps: z.array(statement).max(5),
};
export const reviewDraftV2Schema = z
    .object({ ...draftFields, knowledgeGaps: z.array(reviewGapV2Schema).max(8) })
    .strict();
export const reviewDraftSchema = z
    .object({
    ...draftFields,
    knowledgeGaps: z.array(reviewGapSchema).max(8),
    solutionNotes: solutionNotesSchema.nullable().default(null),
})
    .strict();
// Nest a gap under its observation so constrained decoding can require coaching
// for every unmet criterion without relying on matching two model-generated lists.
const modelCitationSchema = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('source'),
        path: z.string().max(256).refine(safeProjectPath),
        line: z.number().int().positive(),
    })
        .strict(),
    z.object({ kind: z.literal('explanation'), line: z.number().int().positive() }).strict(),
]);
const modelCitations = z.array(modelCitationSchema).min(1).max(3);
/** The exported brief is not learner evidence: the task body already carries the instructions. */
export const REVIEW_EXCLUDED_PATHS = new Set(['WORKSHOP.md']);
/**
 * Saved files whose content is byte-identical to the pinned starter. A starter carries the
 * complete previous solution forward, so these files are reference code, not the learner's work.
 */
export function unchangedStarterPaths(artifact, starter) {
    if (!starter)
        return [];
    const baseline = new Map(starter.files.map((file) => [file.path, file.content]));
    return artifact.files
        .filter((file) => !REVIEW_EXCLUDED_PATHS.has(file.path) && baseline.get(file.path) === file.content)
        .map((file) => file.path);
}
/** The files a review may read, cite and budget: exported instructions and unchanged starter files are left out. */
export function reviewableArtifact(artifact, starter) {
    const unchanged = new Set(unchangedStarterPaths(artifact, starter));
    return {
        ...artifact,
        files: artifact.files.filter((file) => !REVIEW_EXCLUDED_PATHS.has(file.path) && !unchanged.has(file.path)),
    };
}
/** Only nonempty saved lines of reviewable files receive a selectable evidence identity. */
export function createReviewEvidence(artifact, explanation) {
    const references = {};
    let sequence = 0;
    const lines = (content, path) => content.split(/\r?\n/).map((text, index) => {
        if (!text.trim())
            return { text };
        const ref = `e${++sequence}`;
        references[ref] =
            path === undefined
                ? { kind: 'explanation', line: index + 1 }
                : { kind: 'source', path, line: index + 1 };
        return { ref, text };
    });
    const files = reviewableArtifact(artifact).files.map((file) => ({
        path: file.path,
        lines: lines(file.content, file.path),
    }));
    return { references, files, explanation: { lines: lines(explanation) } };
}
const modelCriterion = reviewCriterionSchema.extend({ citations: modelCitations });
const modelGapSchema = reviewGapSchema
    .omit({ criterionId: true })
    .extend({ citations: modelCitations });
export const reviewModelResponseSchema = z
    .object({
    feedback: draftFields.feedback,
    nextSteps: draftFields.nextSteps,
    strengths: z.array(strengthSchema.extend({ citations: modelCitations })).max(3),
    criteria: z
        .array(z.discriminatedUnion('result', [
        modelCriterion.extend({ result: z.literal('met'), gap: z.null() }),
        modelCriterion.extend({ result: z.enum(['partial', 'not_met']), gap: modelGapSchema }),
    ]))
        .min(1)
        .max(8),
    // Null unless the caller supplied a reference solution for a final review.
    solutionNotes: solutionNotesSchema.nullable(),
})
    .strict();
/** Named required properties prevent missing or duplicated rubric entries in model output. */
export function reviewModelSchemaForRubric(ids, references, options = {}) {
    if (!ids.length || ids.length > 8 || new Set(ids).size !== ids.length)
        throw new Error('Invalid review rubric IDs');
    const keys = Object.keys(references);
    if (!keys.length)
        throw new Error('No review evidence');
    // Constrained decoding selects a saved reference, rather than inventing a path/line pair.
    // Keep source text and filenames out of the provider grammar; hydrate them server-side.
    const citations = z
        .array(z.enum(keys))
        .min(1)
        .max(3)
        .transform((items) => items.map((key) => modelCitationSchema.parse(references[key])));
    const gap = modelGapSchema.extend({ citations });
    const observation = modelCriterion.omit({ id: true }).extend({ citations, gap: gap.nullable() });
    return reviewModelResponseSchema
        .omit({ criteria: true, strengths: true, solutionNotes: true })
        .extend({
        criteria: z.object(Object.fromEntries(ids.map((id) => [id, observation]))).strict(),
        // Without a reference solution the grammar only admits null, so ordinary
        // reviews cannot smuggle a worked answer through this field.
        solutionNotes: options.solutionNotes ? solutionNotesSchema : z.null().default(null),
    })
        .transform((value) => ({
        ...value,
        strengths: Object.entries(value.criteria)
            .filter(([, item]) => item.result === 'met')
            .slice(0, 3)
            .map(([criterionId, item]) => ({
            criterionId,
            description: item.feedback,
            citations: item.citations,
        })),
        criteria: Object.entries(value.criteria).map(([id, item]) => ({
            ...item,
            id,
        })),
    }))
        .pipe(reviewModelResponseSchema);
}
/** Source excerpts are reconstructed from immutable files, never rewritten by the model. */
export function normalizeReviewResponse(response, artifact, explanation) {
    const files = new Map(artifact.files.map((file) => [file.path, file.content.split(/\r?\n/)]));
    const hydrate = (references) => references.map((reference) => {
        const lines = reference.kind === 'explanation' ? explanation.split(/\r?\n/) : files.get(reference.path);
        const line = lines?.[reference.line - 1];
        if (!line?.trim())
            throw new Error('Invalid evidence reference');
        // A valid saved line can exceed the display excerpt limit (especially explanations).
        // Keep an exact bounded substring of that line, without inventing or moving evidence.
        const quote = line.trim().slice(0, reference.kind === 'explanation' ? 1000 : 1500);
        return reviewEvidenceSchema.parse(reference.kind === 'explanation'
            ? { kind: 'explanation', quote }
            : {
                kind: 'source',
                path: reference.path,
                startLine: reference.line,
                endLine: reference.line,
                quote,
            });
    });
    return {
        feedback: response.feedback,
        strengths: response.strengths.map((item) => ({ ...item, citations: hydrate(item.citations) })),
        nextSteps: response.nextSteps,
        criteria: response.criteria.map(({ gap: _gap, ...criterion }) => ({
            ...criterion,
            citations: hydrate(criterion.citations),
        })),
        knowledgeGaps: response.criteria.flatMap((criterion) => criterion.gap
            ? [
                {
                    ...criterion.gap,
                    citations: hydrate(criterion.gap.citations),
                    criterionId: criterion.id,
                },
            ]
            : []),
        solutionNotes: response.solutionNotes,
    };
}
const assessedCriterionSchema = reviewCriterionSchema.extend({
    dimension: reviewDimensionSchema.nullable(),
    required: z.boolean(),
});
const labelledCriterionSchema = assessedCriterionSchema.extend({ label: criterionLabelSchema });
const countsSchema = z
    .object({
    met: z.number().int().nonnegative(),
    partial: z.number().int().nonnegative(),
    notMet: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
})
    .strict();
export const reviewMetricsSchema = z
    .object({
    required: countsSchema,
    dimensions: z
        .array(countsSchema
        .extend({
        dimension: reviewDimensionSchema,
        band: z.enum(['demonstrated', 'developing', 'needs_attention', 'not_assessed']),
    })
        .strict())
        .length(4),
})
    .strict();
export function deriveReviewMetrics(criteria) {
    const count = (items) => ({
        met: items.filter((item) => item.result === 'met').length,
        partial: items.filter((item) => item.result === 'partial').length,
        notMet: items.filter((item) => item.result === 'not_met').length,
        total: items.length,
    });
    return reviewMetricsSchema.parse({
        required: count(criteria.filter((item) => item.required)),
        dimensions: reviewDimensionSchema.options.map((dimension) => {
            const counts = count(criteria.filter((item) => item.dimension === dimension));
            const band = counts.total === 0
                ? 'not_assessed'
                : counts.met === counts.total
                    ? 'demonstrated'
                    : counts.met * 2 + counts.partial >= counts.total
                        ? 'developing'
                        : 'needs_attention';
            return { dimension, ...counts, band };
        }),
    });
}
const storedReviewFields = {
    verdict: z.enum(['passed', 'revision_requested']),
    rubricRevision: z.string().regex(/^[a-f0-9]{64}$/),
    execution: z.literal('not_verified'),
    metrics: reviewMetricsSchema,
    usage: z
        .object({
        inputTokens: z.number().int().nonnegative(),
        outputTokens: z.number().int().nonnegative(),
    })
        .strict(),
    durationMs: z.number().int().nonnegative(),
};
function reviewIntegrity(review, context) {
    const expected = deriveReviewMetrics(review.criteria);
    if (new Set(review.criteria.map((item) => item.id)).size !== review.criteria.length)
        context.addIssue({ code: 'custom', message: 'Criterion IDs must be unique' });
    if (expected.required.total === 0 ||
        (review.verdict === 'passed') !== (expected.required.met === expected.required.total))
        context.addIssue({
            code: 'custom',
            path: ['verdict'],
            message: 'Verdict must match required criteria',
        });
    if (JSON.stringify(expected) !== JSON.stringify(review.metrics))
        context.addIssue({
            code: 'custom',
            path: ['metrics'],
            message: 'Metrics must match criterion observations',
        });
}
/** Retained reviews written by `llm-review-v3`; readers must keep accepting them unchanged. */
export const workshopReviewV2Schema = reviewDraftV2Schema
    .extend({
    ...storedReviewFields,
    version: z.literal(2),
    criteria: z.array(assessedCriterionSchema).min(1).max(8),
})
    .strict()
    .superRefine(reviewIntegrity);
/** Current stored review: labelled criteria, explicit "not done" statements and optional solution notes. */
export const workshopReviewSchema = reviewDraftSchema
    .extend({
    ...storedReviewFields,
    version: z.literal(3),
    criteria: z.array(labelledCriterionSchema).min(1).max(8),
    solutionNotes: solutionNotesSchema.nullable(),
})
    .strict()
    .superRefine(reviewIntegrity);
/** Learner-facing criterion title for any stored review version. */
export function criterionLabel(criterion) {
    if (criterion.label)
        return criterion.label;
    const words = criterion.id.replaceAll('-', ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
}
